"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const EventEmitter = require("wolfy87-eventemitter");
const moment = require("moment");
const TypeScriptUtils_1 = require("../Common/TypeScriptUtils");
const RtcCommand_1 = require("../Engine/Command/RtcCommand");
const DomainCommandHandler_1 = require("../Engine/Command/DomainCommandHandler");
const DomainEventHandler_1 = require("../Engine/Event/DomainEventHandler");
const Attendee_1 = require("./Protocol/Attendee");
const SessionDefinition_1 = require("./SessionDefinition");
const SessionTicket_1 = require("./Protocol/SessionTicket");
const SessionDto_1 = require("./Protocol/SessionDto");
const SessionCommand_1 = require("./SessionCommand");
const SessionEvent_1 = require("./SessionEvent");
const Session_1 = require("./Session");
const SessionApplicationServiceBase_1 = require("./SessionApplicationServiceBase");
const ISessionApplicationService_1 = require("./ISessionApplicationService");
class SessionClientApplicationService extends SessionApplicationServiceBase_1.SessionApplicationServiceBase {
    constructor(sessionFactory, sessionRepository, cmdHub, evtHub) {
        super(sessionFactory, sessionRepository, cmdHub, evtHub);
        this._curSessionUser = Attendee_1.Attendee.Nobody;
        this.errorOccurred = new EventEmitter();
        this._commandHandler = new DomainCommandHandler_1.DomainCommandHandler(SessionCommand_1.SessionCommand.Category, this._logger, cmdHub);
        this._commandHandler.registerAsyncCommandHandler(SessionCommand_1.SessionCommand.StartNewSession, async (rtcCmd) => this.OnStartNewSessionCommand(rtcCmd));
        this._eventHandler = new DomainEventHandler_1.DomainEventHandler(SessionEvent_1.SessionEvent.Category, this._logger);
        this._eventHandler.registerAsyncEventHandler(SessionEvent_1.SessionEvent.SessionFailed, async (evt) => this.OnSessionFailed(evt));
    }
    get CurSessionUser() { return this._curSessionUser; }
    async initAsync(userId, userName) {
        this._curSessionUser = Attendee_1.Attendee.from(userId, userName);
        this._cmdHub.errorOccurred.addListener(SessionCommand_1.SessionCommand.Category, (error) => this.reportError(error));
        this._evtHub.errorOccurred.addListener(SessionEvent_1.SessionEvent.Category, (error) => this.reportError(error));
        this._cmdHub.registerCommandHandler(SessionCommand_1.SessionCommand.Category, this);
        this._evtHub.registerEventHandler(SessionEvent_1.SessionEvent.Category, this);
    }
    async terminateAsync() {
        this._evtHub.unRegisterEventHandler(SessionEvent_1.SessionEvent.Category);
        this._cmdHub.unRegisterCommandHandler(SessionCommand_1.SessionCommand.Category);
        this._evtHub.errorOccurred.removeListener(SessionEvent_1.SessionEvent.Category, this.reportError);
        this._cmdHub.errorOccurred.removeListener(SessionCommand_1.SessionCommand.Category, this.reportError);
        await this.clearAllLocalDataAsync();
        this.errorOccurred.removeAllListeners();
        this._curSessionUser = Attendee_1.Attendee.Nobody;
    }
    handleCommand(cmd) {
        this._commandHandler.handleCommand(cmd);
    }
    async handleCommandAsync(cmd) {
        if (!TypeScriptUtils_1.isNullOrEmpty(cmd.targetId)) {
            const sessionId = cmd.targetId;
            const session = await this._sessionRepo.SessionOfIdAsync(sessionId);
            if (session !== Session_1.Session.INVALID_SESSION) {
                await session.handleCommandAsync(cmd);
                return;
            }
        }
        await this._commandHandler.handleCommandAsync(cmd);
    }
    async OnStartNewSessionCommand(rtcCmd) {
        const content = rtcCmd.contentTo();
        const sender = new Attendee_1.Attendee(content.Item1);
        const self = new Attendee_1.Attendee(content.Item2);
        const incommingSt = new SessionTicket_1.SessionTicket(content.Item3);
        const session = await this._sessionRepo.SessionOfIdAsync(incommingSt.id);
        if (session != null && session !== Session_1.Session.INVALID_SESSION) {
            await session.handleCommandAsync(rtcCmd);
            return;
        }
        switch (rtcCmd.type) {
            case RtcCommand_1.CmdType.RequestExecute:
                const selfAttendee = Attendee_1.Attendee.from(this._curSessionUser.id, this._curSessionUser.name);
                const newSession = this._sessionFactory.CreateNewSession(incommingSt, [selfAttendee]);
                newSession.Save();
                await newSession.handleCommandAsync(rtcCmd);
                break;
        }
    }
    handleEvent(e) {
        if (e.isLocal)
            return;
        if (!TypeScriptUtils_1.isNullOrEmpty(e.entityId)) {
            const sessionId = e.entityId;
            const session = this._sessionRepo.SessionOfId(sessionId);
            if (session !== Session_1.Session.INVALID_SESSION) {
                session.handleEvent(e);
                return;
            }
        }
        this._eventHandler.handleEvent(e);
    }
    async handleEventAsync(e) {
        if (e.isLocal)
            return;
        if (!TypeScriptUtils_1.isNullOrEmpty(e.entityId)) {
            const sessionId = e.entityId;
            const session = await this._sessionRepo.SessionOfIdAsync(sessionId);
            if (session !== Session_1.Session.INVALID_SESSION) {
                await session.handleEventAsync(e);
                return;
            }
        }
        await this._eventHandler.handleEventAsync(e);
    }
    async OnSessionFailed(evt) {
        const body = evt.bodyTo();
        const st = new SessionTicket_1.SessionTicket(body.Item1);
        const remoteAttendee = new Attendee_1.Attendee(body.Item2);
        const selfAttendee = Attendee_1.Attendee.from(this._curSessionUser.id, this._curSessionUser.name);
        const newSession = this._sessionFactory.CreateNewSession(st, [selfAttendee]);
        newSession.Save();
        await newSession.handleEventAsync(evt);
    }
    subscribeEventHandler(eventName, onEventHandler) {
        return this._evtHub.subscribeEventHandler(SessionEvent_1.SessionEvent.Category, eventName, onEventHandler, (err) => this.reportError(err));
    }
    subscribeEventAsyncHandler(eventName, onEventHandler) {
        return this._evtHub.subscribeEventAsyncHandler(SessionEvent_1.SessionEvent.Category, eventName, onEventHandler, (err) => this.reportError(err));
    }
    requestToStartVideoAudioSessionWith(creator, needRecord = false, attendees) {
        const result = this.execute(() => super.requestToStartVideoAudioSessionWith(creator, needRecord, attendees), SessionTicket_1.SessionTicket.UNKNOWN_SESSION_TICKET);
        return result;
    }
    async requestToStartVideoAudioSessionWithAsync(creator, needRecord = false, attendees) {
        const result = await this.executeAsync(async () => await super.requestToStartVideoAudioSessionWithAsync(creator, needRecord, attendees), SessionTicket_1.SessionTicket.UNKNOWN_SESSION_TICKET);
        return result;
    }
    requestToStartAudioSessionWith(creator, needRecord = false, attendees) {
        const result = this.execute(() => super.requestToStartAudioSessionWith(creator, needRecord, attendees), SessionTicket_1.SessionTicket.UNKNOWN_SESSION_TICKET);
        return result;
    }
    requestToStartShareScreenSessionWith(creator, needRecord = false, attendees) {
        const result = this.execute(() => super.requestToStartShareScreenSessionWith(creator, needRecord, attendees), SessionTicket_1.SessionTicket.UNKNOWN_SESSION_TICKET);
        return result;
    }
    requestToStartVideoLiveSessionWith(creator, needRecord = false, attendees) {
        const result = this.execute(() => super.requestToStartVideoLiveSessionWith(creator, needRecord, attendees), SessionTicket_1.SessionTicket.UNKNOWN_SESSION_TICKET);
        return result;
    }
    requestToStartDataSessionWith(creator, attendees) {
        const result = this.execute(() => {
            if (attendees.length === 0)
                throw new ISessionApplicationService_1.SessionApplicationServiceError(`attendees should not be empty.`);
            const newSession = this._sessionFactory.CreateNewSessionByCreator(SessionDefinition_1.SessionType.Data, creator, false, attendees);
            newSession.Save();
            return newSession.ToSessionTicket();
        }, SessionTicket_1.SessionTicket.UNKNOWN_SESSION_TICKET);
        return result;
    }
    async importSessionAsync(st) {
        const result = await this.executeAsync(async () => await super.importSessionAsync(st), SessionTicket_1.SessionTicket.UNKNOWN_SESSION_TICKET);
        return result;
    }
    async querySessionAsync(sessionId) {
        const result = await this.executeAsync(async () => await super.querySessionAsync(sessionId), SessionDto_1.SessionDto.INVALID_SESSION);
        return result;
    }
    async startSessionAsync(starter, sessionId, videoContainer) {
        await this.executeAsync(async () => {
            await super.startSessionAsync(starter, sessionId, videoContainer);
        });
    }
    async startDataSessionAsync(starter, sessionId, localDataProvider, remoteDataReceiver, ctor) {
        await this.executeAsync(async () => {
            const session = await this._sessionRepo.SessionOfIdAsync(sessionId);
            if (session === Session_1.Session.INVALID_SESSION)
                throw new ISessionApplicationService_1.SessionApplicationServiceError(`No session of ${sessionId}`);
            await session.StartDataSessionAsync(starter, localDataProvider, remoteDataReceiver, ctor);
        });
    }
    async stopSessionAsync(attendee, sessionId) {
        await this.executeAsync(async () => {
            await super.stopSessionAsync(attendee, sessionId);
        });
    }
    async exitSessionAsync(attendee, sessionId) {
        await this.executeAsync(async () => {
            await super.exitSessionAsync(attendee, sessionId);
        });
    }
    async joinSessionAsync(attendee, sessionId, videoContainer = null) {
        await this.executeAsync(async () => {
            await super.joinSessionAsync(attendee, sessionId, videoContainer);
        });
    }
    async joinDataSessionAsync(attendee, sessionId, localDataProvider, remoteDataReceiver, ctor) {
        await this.executeAsync(async () => {
            const session = this._sessionRepo.SessionOfId(sessionId);
            if (session === Session_1.Session.INVALID_SESSION)
                throw new ISessionApplicationService_1.SessionApplicationServiceError(`No session of ${sessionId}`);
            await session.JoinDataSessionAsync(attendee, localDataProvider, remoteDataReceiver, ctor);
        });
    }
    async rejectSessionAsync(attendee, sessionId) {
        await this.executeAsync(async () => {
            await super.rejectSessionAsync(attendee, sessionId);
        });
    }
    querySessionDuration(sessionId) {
        const result = this.execute(() => super.querySessionDuration(sessionId), moment.duration(0));
        return result;
    }
    queryDurationOfAttendeeSession(sessionId, attendee) {
        const result = this.execute(() => super.queryDurationOfAttendeeSession(sessionId, attendee), moment.duration(0));
        return result;
    }
    switchToSessionNextVideoSource(sessionId, attendee) {
        this.execute(() => {
            const session = this._sessionRepo.SessionOfId(sessionId);
            if (session === Session_1.Session.INVALID_SESSION)
                throw new ISessionApplicationService_1.SessionApplicationServiceError(`No session of ${sessionId}`);
            //session.SwitchToNextVideoSource(attendee);
        });
    }
    async clearAllLocalDataAsync() {
        await this.executeAsync(async () => {
            await this._sessionRepo.ClearAsync();
        });
    }
    async executeAsync(operation, defaultReturnValue = {}, memberName = "") {
        try {
            const localUser = this._curSessionUser;
            if (localUser === Attendee_1.Attendee.Nobody)
                throw new ISessionApplicationService_1.SessionApplicationServiceError("local user should be valid user");
            return await operation();
        }
        catch (err) {
            const errorMsg = `${memberName || ""}: ${err.message}`;
            const appServiceError = new ISessionApplicationService_1.SessionApplicationServiceError(errorMsg, err);
            this.reportError(appServiceError);
            return defaultReturnValue;
        }
    }
    execute(operation, defaultReturnValue = {}, memberName = "") {
        try {
            const localUser = this._curSessionUser;
            if (localUser === Attendee_1.Attendee.Nobody)
                throw new ISessionApplicationService_1.SessionApplicationServiceError("local user should be valid user");
            return operation();
        }
        catch (err) {
            const errorMsg = `${memberName || ""}: ${err.message}`;
            const appServiceError = new ISessionApplicationService_1.SessionApplicationServiceError(errorMsg, err);
            this.reportError(appServiceError);
            return defaultReturnValue;
        }
    }
    reportError(error) {
        try {
            const sessionAppServiceError = (error instanceof ISessionApplicationService_1.SessionApplicationServiceError) ? error : new ISessionApplicationService_1.SessionApplicationServiceError(error.message, error);
            this.errorOccurred.emitEvent(ISessionApplicationService_1.SessionApplicationServiceErrorName, [sessionAppServiceError]);
        }
        catch (e) {
            // Prevent outside error handler throwing error again.
            this._logger.error(e.message, e);
        }
    }
    GetType() { return "SessionClientApplicationService"; }
}
exports.SessionClientApplicationService = SessionClientApplicationService;
//# sourceMappingURL=SessionClientApplicationService.js.map